import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Button from '../../../../components/Button';
import ModuleSection from '../ModuleSection';
import ArticleEditModal from '../ArticleEditModal';
import blogStorage from '../../utils/storage';
import { renderMarkdown } from '../../utils/markdown';
import './index.scss';

const ArticleDetail = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const pathParts = location.pathname.split('/');
  const id = pathParts[3]; // 从路径中获取文章ID
  const [article, setArticle] = useState(null);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);

  useEffect(() => {
    loadArticle();
  }, [id]);

  const loadArticle = () => {
    try {
      const articles = blogStorage.getArticles();
      const categories = blogStorage.getCategories();
      
      const foundArticle = articles.find(art => art.id === id);
      if (!foundArticle) {
        setError('文章未找到');
        setLoading(false);
        return;
      }

      const foundCategory = categories.find(cat => cat.id === foundArticle.categoryId);
      
      setArticle(foundArticle);
      setCategory(foundCategory);
      
      // 增加浏览量
      blogStorage.updateArticle(id, { 
        viewCount: (foundArticle.viewCount || 0) + 1 
      });
      
      setLoading(false);
    } catch (err) {
      console.error('加载文章失败:', err);
      setError('加载文章失败');
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/blog/articles');
  };

  const handleEdit = () => {
    setEditModalOpen(true);
  };

  const handleSaveArticle = (savedArticle) => {
    // 重新加载文章数据
    loadArticle();
    setEditModalOpen(false);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // 使用新的markdown渲染器
  const renderContent = (content) => {
    return renderMarkdown(content);
  };

  if (loading) {
    return (
      <div className="article-detail">
        <ModuleSection title="加载中...">
          <div className="loading-state">
            <p>正在加载文章内容...</p>
          </div>
        </ModuleSection>
      </div>
    );
  }

  if (error) {
    return (
      <div className="article-detail">
        <ModuleSection title="错误">
          <div className="error-state">
            <p>{error}</p>
            <Button onClick={handleBack}>
              返回文章列表
            </Button>
          </div>
        </ModuleSection>
      </div>
    );
  }

  if (!article) {
    return null;
  }

  return (
    <div className="article-detail">
      {/* 文章头部信息 */}
      <ModuleSection 
        title="文章详情"
        actions={
          <div className="article-actions">
            <Button onClick={handleBack}>
              返回列表
            </Button>
            <Button type="primary" onClick={handleEdit}>
              编辑文章
            </Button>
          </div>
        }
        variant="info"
      >
        <div className="article-header">
          <h1 className="article-title">{article.title}</h1>
          
          <div className="article-meta">
            <div className="meta-row">
              <span className="meta-item">
                <span className="meta-label">分类：</span>
                <span className="meta-value category-tag">
                  {category?.name || '未分类'}
                </span>
              </span>
              
              {article.isTop && (
                <span className="meta-item">
                  <span className="top-badge">置顶</span>
                </span>
              )}
            </div>
            
            <div className="meta-row">
              <span className="meta-item">
                <span className="meta-label">发布时间：</span>
                <span className="meta-value">
                  {formatDate(article.publishedAt || article.createdAt)}
                </span>
              </span>
              
              {article.updatedAt !== article.createdAt && (
                <span className="meta-item">
                  <span className="meta-label">更新时间：</span>
                  <span className="meta-value">
                    {formatDate(article.updatedAt)}
                  </span>
                </span>
              )}
            </div>
            
            <div className="meta-row">
              <span className="meta-item">
                <span className="meta-label">阅读量：</span>
                <span className="meta-value">{article.viewCount || 0}</span>
              </span>
              
              <span className="meta-item">
                <span className="meta-label">点赞：</span>
                <span className="meta-value">{article.likeCount || 0}</span>
              </span>
              
              <span className="meta-item">
                <span className="meta-label">字数：</span>
                <span className="meta-value">{article.wordCount || 0}</span>
              </span>
            </div>
            
            {article.tags && article.tags.length > 0 && (
              <div className="meta-row">
                <span className="meta-label">标签：</span>
                <div className="tags-list">
                  {article.tags.map(tag => (
                    <span key={tag} className="tag-item">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </ModuleSection>

      {/* 文章内容 */}
      <ModuleSection 
        title="文章内容"
        variant="editor"
        className="article-content-section"
      >
        <div className="article-content">
          <div 
            className="content-body"
            dangerouslySetInnerHTML={{ 
              __html: renderContent(article.content) || '<p>暂无内容</p>' 
            }}
          />
        </div>
      </ModuleSection>

      {/* 编辑模态弹窗 */}
      <ArticleEditModal
        isOpen={editModalOpen}
        onClose={handleCloseEditModal}
        mode="edit"
        articleId={article?.id}
        onSave={handleSaveArticle}
      />
    </div>
  );
};

export default ArticleDetail;
